import type { RouterIds } from '@/Router';
import { useAuthStore, useUserCaps } from '@/Utility/auth/Auth';
import { useUser } from '@/Utility/hooks';
import { MediaItemSizeEnum } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import {
  faAddressCard,
  faBars,
  faChartPie,
  faCircleQuestion,
  faColumns,
  faGear,
  faList,
  faRightToBracket,
  faSignOutAlt,
  faSitemap,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Category } from '@mui/icons-material';
import { Avatar, Box, Button, ListItem, Menu, MenuItem } from '@mui/material';
import { type ToOptions, type ToPathOption, Link as TsrLink } from '@tanstack/react-router';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import React, { useMemo } from 'react';
import { FormRendererV2 } from '../../form';
import ModalButton from '../../inputs/ModalButton';
import { getMediaItemUrl } from '../../posts/utils';
import type { Header_ZnglyInfo_Desktop$data } from './__generated__/Header_ZnglyInfo_Desktop.graphql';

import styles from './styles.module.css';

type UserDropdownItemDefault = {
  name: string;
  url: RouterIds;
  linkType?: 'tsr-router' | 'inertia' | 'a' | null | undefined;
  icon: React.JSX.Element;
  action?: () => void;
  isCurrentOption?: boolean;
};

type UserDropdownItem =
  | UserDropdownItemDefault
  | (UserDropdownItemDefault & { linkType: 'tsr-router'; url: ToOptions['to'] });

// function keeps a config of the header dd items
const useUserHeaderDropdownItems = (): UserDropdownItem[] => {
  const { pathname } = window.location;
  const info = useAppStore((state) => state.info);
  const isDashboard = useAppStore((state) => state.isDashboard);
  const hasAnalytics = useAppStore((state) => state?.info?.hasAnalytics);

  const signout = useAuthStore((state) => state.signout);

  const currentRouter = useMemo(() => (isDashboard ? 'tsr-router' : 'inertia'), [isDashboard]);
  const canManageCategories = useUserCaps(['manage_categories']);
  const userType = useAuthStore((state) => state.user?.role);

  const items = useMemo<UserDropdownItem[]>(
    () =>
      (
        [
          userType === 'guest' && {
            name: 'Log In',
            linkType: 'tsr-router',
            url: '/auth/login',
            icon: <FontAwesomeIcon icon={faRightToBracket} />,
          },
          hasAnalytics && {
            name: 'Analytics',
            linkType: 'tsr-router',
            url: '/dash/analytics',
            icon: <FontAwesomeIcon icon={faChartPie} />,
          },
          {
            name: 'CRM',
            linkType: 'tsr-router',
            url: '/dash/crm',
            icon: <FontAwesomeIcon icon={faAddressCard} />,
          },
          {
            name: 'Catalog',
            linkType: 'tsr-router',
            url: '/dash/catalog',
            icon: <FontAwesomeIcon icon={faVideo} />,
          },
          {
            name: 'Znglists',
            linkType: 'tsr-router',
            url: '/dash/znglists',
            icon: <FontAwesomeIcon icon={faList} />,
          },
          {
            name: 'Hubs',
            linkType: 'tsr-router',
            url: '/dash/hubs/hubs',
            icon: <FontAwesomeIcon icon={faSitemap} />,
          },
          canManageCategories && {
            name: 'Categories',
            linkType: 'tsr-router',
            url: '/dash/content/categories',
            icon: <Category />,
          },
          {
            name: 'Settings',
            linkType: 'tsr-router',
            url: '/dash/settings',
            icon: <FontAwesomeIcon icon={faGear} />,
          },
          userType === 'super_admin' && {
            name: 'Super Admin',
            url: info?.dashboardUrl,
            linkType: 'a',
            icon: <FontAwesomeIcon icon={faColumns} />,
          },
          {
            name: 'Service Desk',
            linkType: 'tsr-router',
            url: '/service-desk',
            icon: <FontAwesomeIcon icon={faCircleQuestion} />,
          },
          {
            name: 'Log Out',
            icon: <FontAwesomeIcon icon={faSignOutAlt} />,
            action: () => {
              signout();
            },
          },
        ] as UserDropdownItem[]
      )
        .filter((item) => {
          // if no user then only show login
          if (userType === 'guest') return item.url === '/auth/login';
          return !!item;
        })
        .map(
          // map through the items and set the current option if the pathname matches
          (item) => {
            let linkType = item?.linkType ?? null;

            if (
              !!item?.linkType &&
              item.linkType !== currentRouter &&
              (['/auth/login'] as Array<ToOptions['to']>).includes(item?.url as ToOptions['to']) === false // exclude login from linkType change
            ) {
              linkType = 'a';
            }

            return {
              ...item,
              isCurrentOption: pathname === item.url,
              linkType,
            };
          }
        ),
    [userType, hasAnalytics, pathname, currentRouter, info, signout, canManageCategories]
  );

  return items;
};

// DropDown Link
const DDLink = ({ children, url, linkType, action }: { children: React.ReactNode } & Partial<UserDropdownItem>) => {
  if (url && linkType === 'tsr-router') {
    return (
      <TsrLink to={url as ToPathOption} className={styles.DDLink} onClick={action} preload="intent">
        {children}
      </TsrLink>
    );
  }

  if (url && linkType === 'inertia') {
    return (
      <TsrLink to={url as ToPathOption} style={{ cursor: 'pointer' }} className={styles.DDLink} onClick={action}>
        {children}
      </TsrLink>
    );
  }

  return (
    <a style={{ cursor: 'pointer' }} className={styles.DDLink} {...(!!action ? { onClick: action } : { href: url })}>
      {children}
    </a>
  );
};

const DDLinkMemo = React.memo(DDLink);

type HeaderDDMenuProps = {
  disabled?: boolean;
  onlyShowIfLoggedIn?: boolean;
};
export const HeaderDDMenu = (props: HeaderDDMenuProps) => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'userDropdownMenuDesktop' });
  const { user } = useUser();

  const items = useUserHeaderDropdownItems();

  if (props?.onlyShowIfLoggedIn && !user) return null;

  return (
    <>
      <Button {...bindTrigger(popupState)} className={`zng-user-menu ${styles.dropdownButton}`}>
        {!!user ? (
          <>
            <Avatar
              alt={user?.username ?? user?.name ?? 'user avatar'}
              src={getMediaItemUrl(user?.meta?.profileImage, MediaItemSizeEnum.Thumbnail) ?? user?.avatar?.url ?? ''}
            />
            {user?.username ?? user?.name}
          </>
        ) : (
          <FontAwesomeIcon className={styles.dropdownButtonIcon} icon={faBars} />
        )}
      </Button>
      <Menu {...bindMenu(popupState)} className={styles.dropdownMenu}>
        {items.map(({ icon, name, isCurrentOption, ...linkProps }) => {
          return (
            <MenuItem
              className={styles.DDMenuListItem}
              key={`${name}_${linkProps.url}`}
              disabled={isCurrentOption || props?.disabled}
              onClick={popupState.close}
            >
              <DDLinkMemo {...linkProps}>
                <div>
                  {icon} {name}
                </div>
              </DDLinkMemo>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

type HeaderLogoLinkProps = {
  znglyInfo: Header_ZnglyInfo_Desktop$data['znglyInfo'];
  style?: React.CSSProperties;
  homeOnly?: boolean;
  children: React.ReactNode;
};

export const HeaderLogoLink = (props: HeaderLogoLinkProps) => {
  const info = useAppStore((state) => state.info);
  const isDashboard = useAppStore((state) => state.isDashboard);
  const isPageLoading = useAppStore((state) => state.isPageLoading);

  const { url, homeUrl } = useMemo(() => {
    return {
      url: new URL(props?.znglyInfo?.headerLogoLink ?? info?.homeUrl ?? '', window.location.origin),
      homeUrl: new URL(info?.homeUrl ?? '', window.location.origin),
    };
  }, [props?.znglyInfo?.headerLogoLink, info?.homeUrl]);

  if (url.hostname === homeUrl.hostname && !isDashboard) {
    return (
      <TsrLink to="/" style={{ cursor: 'pointer', ...props?.style }} disabled={isPageLoading} preload="intent">
        {props.children}
      </TsrLink>
    );
  }

  return (
    <a href={props?.homeOnly ? '/' : (props?.znglyInfo?.headerLogoLink ?? '/')} rel="prefetch" style={props?.style}>
      {props.children}
    </a>
  );
};

type HeaderButtonsProps = {
  znglyInfo: Header_ZnglyInfo_Desktop$data['znglyInfo'];
  type: 'mobile' | 'desktop';
};

export const HeaderButtons = ({ znglyInfo, type }: HeaderButtonsProps) => {
  if (type === 'mobile') {
    return znglyInfo?.headerButtons?.map?.((headerButton) => {
      const button = (
        // @ts-ignore - component "a" does not exist on type "Box" since the migration to MUI v6
        <Box
          component="a"
          {...(!headerButton?.form ? { href: headerButton?.url, target: '_blank', rel: 'noreferrer' } : {})}
          sx={{
            width: '100%',
            padding: '0.5em 1em',
            borderRadius: '0.5em',
            textAlign: 'center',
            backgroundColor: headerButton?.bgColor,
            color: headerButton?.textColor,
            transition: 'all 0.25s',
            '&:hover': {
              backgroundColor: headerButton?.bgHvColor,
              color: headerButton?.textHvColor,
            },
          }}
        >
          {headerButton?.name}
        </Box>
      );

      if (!!headerButton?.form) {
        const formNode = headerButton?.form?.node;

        return (
          <ListItem key={`${headerButton?.name}_${headerButton?.url}`}>
            <ModalButton
              onClose={() => {
                window.location.hash = '';
              }}
              body={({ closeModal }) => (
                <FormRendererV2
                  formId={formNode.id}
                  schema={formNode.schema ?? ''}
                  uiSchema={formNode.uiSchema ?? ''}
                  title={formNode.title}
                  style={{ height: '100%' }}
                  metadata={{
                    ...formNode?.metadata,
                    background: {
                      img: formNode?.imageUrl ?? undefined,
                    },
                    marketingConsent: formNode?.marketingConsent ?? undefined,
                    password: formNode?.password ?? undefined,
                  }}
                  hooks={{
                    afterSubmit() {
                      closeModal();
                    },
                  }}
                />
              )}
              button={button}
            />
          </ListItem>
        );
      }
      return <ListItem key={`${headerButton?.name}_${headerButton?.url}`}>{button}</ListItem>;
    });
  }

  return (
    <Box className={styles.headerButtons}>
      <ul>
        {znglyInfo?.headerButtons?.map((headerButton) => {
          const button = (
            // @ts-ignore - component "a" does not exist on type "Box" since the migration to MUI v6
            // It still works as expected, but will be replaced by using pigment css in the future
            // When it is available - we should fix this
            <Box
              component="a"
              className={styles.headerButton}
              {...(!headerButton?.form ? { href: headerButton?.url, target: '_blank', rel: 'noreferrer' } : {})}
              sx={{
                cursor: 'pointer',
                backgroundColor: headerButton?.bgColor,
                color: headerButton?.textColor,
                '&:hover': {
                  backgroundColor: headerButton?.bgHvColor,
                  color: headerButton?.textHvColor,
                },
                whiteSpace: 'nowrap',
              }}
            >
              {headerButton?.name}
            </Box>
          );

          if (!!headerButton?.form) {
            const formNode = headerButton?.form?.node;

            return (
              <li key={`${headerButton?.name}_${headerButton?.url}`}>
                <ModalButton
                  onClose={() => {
                    window.location.hash = '';
                  }}
                  body={({ closeModal }) => (
                    <FormRendererV2
                      formId={formNode.id}
                      schema={formNode.schema ?? ''}
                      uiSchema={formNode.uiSchema ?? ''}
                      title={formNode.title}
                      style={{ height: '100%' }}
                      metadata={{
                        ...formNode?.metadata,
                        background: {
                          img: formNode?.imageUrl ?? undefined,
                        },
                        marketingConsent: formNode?.marketingConsent ?? undefined,
                        password: formNode?.password ?? undefined,
                      }}
                      hooks={{
                        afterSubmit() {
                          closeModal();
                        },
                      }}
                    />
                  )}
                  button={button}
                />
              </li>
            );
          }

          return <li key={`${headerButton?.name}_${headerButton?.url}`}>{button}</li>;
        })}
      </ul>
    </Box>
  );
};
